import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import { ExperienciaCard } from "@/components/experiencia/ExperienciaCard";
import { WHATSAPP_NUMBER } from "@/lib/api";
import {
  HISTORIA,
  LUGAR,
  LLEGADA,
  RECOMENDACIONES,
  FAQ_PUBLIC,
  PUBLIC_CARDS,
  HERO_QUOTE_PUBLIC,
  AMENIDADES,
} from "@/lib/experienciaContent";

const HERO_IMG =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

export default function Experiencia() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openWA = (message) => {
    const msg = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
  };

  return (
    <div data-testid="experiencia-page" className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <PageSEO
        title="La experiencia · Historia, lugar, llegada y amenidades · Villas Pura Vida"
        description="Conoce la experiencia completa de Villas Pura Vida en Barra de Galindo, Tuxpan Veracruz: historia del lugar, cómo llegar desde CDMX, amenidades frente al mar, y preguntas frecuentes antes de reservar."
        keywords="experiencia villas pura vida, barra de galindo tuxpan, como llegar tuxpan desde cdmx, playa semivirgen veracruz, amenidades hospedaje tuxpan"
        path="/experiencia"
        imageAlt="La experiencia de Villas Pura Vida en Barra de Galindo"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_PUBLIC.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
            },
          })),
        }}
      />      <Header variant="home" />

      {/* Hero */}
      <section className="relative min-h-[92vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/30 via-stone-900/20 to-stone-900/85" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-44 md:pt-52 pb-24 min-h-[92vh] flex flex-col justify-end">
          <div className="max-w-3xl">
            <p className="text-eyebrow text-stone-200 mb-6 fade-up">La experiencia</p>
            <h1
              data-testid="experiencia-hero-title"
              className="font-serif-display text-white text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight mb-6 fade-up fade-up-delay-1"
            >
              Bienvenido a{" "}
              <span className="block italic font-light text-[#F2CC8F]">Villas Pura Vida.</span>
            </h1>
            <p className="text-stone-100 text-lg md:text-xl max-w-2xl mb-10 leading-relaxed fade-up fade-up-delay-2">
              {HERO_QUOTE_PUBLIC}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-up fade-up-delay-3">
              <a
                href="#comenzar"
                data-testid="experiencia-cta-comenzar"
                className="inline-flex items-center justify-center gap-3 bg-[#E07A5F] hover:bg-[#D3674B] text-white px-10 py-4 text-sm uppercase tracking-[0.22em] font-semibold transition-all hover:-translate-y-0.5"
              >
                Comenzar
                <i className="fa-solid fa-arrow-down text-xs" />
              </a>
              <Link
                to="/#villas"
                data-testid="experiencia-cta-reservar"
                className="inline-flex items-center justify-center gap-2 border border-white/60 text-white hover:bg-white hover:text-stone-900 px-8 py-4 text-sm uppercase tracking-[0.22em] font-semibold transition-all"
              >
                Reservar
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Card grid — the invitation */}
      <section id="comenzar" className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-16 max-w-3xl">
          <p className="text-eyebrow text-stone-500 mb-4">Un recorrido</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900">
            Antes de hablar de precios,
            <br />
            <span className="italic text-stone-500">déjanos mostrarte por qué familias regresan.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PUBLIC_CARDS.map((card) => (
            <ExperienciaCard
              key={card.label}
              icon={card.icon}
              label={card.label}
              body={card.body}
              href={card.href}
              testId={`experiencia-card-${card.label.toLowerCase().replace(/\s/g, "-")}`}
            />
          ))}
        </div>
      </section>

      {/* Historia */}
      <section id="historia" className="bg-stone-900 text-stone-100 py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="text-eyebrow text-[#F2CC8F] mb-6">{HISTORIA.eyebrow}</p>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.98] text-white mb-12">
            {HISTORIA.title}
          </h2>
          <div className="space-y-6 text-lg leading-relaxed text-stone-200 max-w-3xl">
            {HISTORIA.body.map((p, i) => (
              <p key={i} className={i === HISTORIA.body.length - 1 ? "font-serif-display italic text-2xl text-[#F2CC8F]" : ""}>
                {p}
              </p>
            ))}
          </div>
          <div className="mt-16 pt-10 border-t border-stone-700 max-w-2xl">
            <p className="font-serif-display italic text-2xl md:text-3xl text-white leading-tight">
              &ldquo;{HISTORIA.quote}&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Lugar */}
      <section id="lugar" className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-16 max-w-3xl">
          <p className="text-eyebrow text-stone-500 mb-4">{LUGAR.eyebrow}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900">
            {LUGAR.title}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {LUGAR.items.map((it) => (
            <div key={it.label} className="border-t border-stone-300 pt-6" data-testid={`experiencia-lugar-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
              <i className={`fa-solid fa-${it.icon} text-xl text-[#E07A5F] mb-4 block`} />
              <h3 className="font-serif-display text-2xl text-stone-900 mb-2">{it.label}</h3>
              <p className="text-stone-600 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Llegada */}
      <section id="llegar" className="bg-stone-100 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <p className="text-eyebrow text-stone-500 mb-4">{LLEGADA.eyebrow}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900 mb-6">
              {LLEGADA.title}
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed">{LLEGADA.intro}</p>
          </div>
          <ol className="space-y-8">
            {LLEGADA.references.map((r) => (
              <li key={r.step} className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-start pb-8 border-b border-stone-300">
                <span className="font-serif-display text-4xl md:text-5xl text-stone-400 italic">{r.step}</span>
                <div>
                  <h3 className="font-serif-display text-2xl text-stone-900 mb-1">{r.label}</h3>
                  <p className="text-stone-600">{r.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Amenidades */}
      <section id="amenidades" className="bg-stone-100 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <p className="text-eyebrow text-stone-500 mb-4">{AMENIDADES.eyebrow}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900 mb-6">
              {AMENIDADES.title}
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed">{AMENIDADES.intro}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {AMENIDADES.groups.map((g) => (
              <div
                key={g.label}
                className="bg-white border border-stone-200 p-8"
                data-testid={`experiencia-amenidades-${g.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <i className={`fa-solid fa-${g.icon} text-2xl text-[#E07A5F]`} />
                  <h3 className="font-serif-display text-2xl text-stone-900">{g.label}</h3>
                </div>
                <ul className="space-y-3">
                  {g.items.map((item) => (
                    <li key={item} className="flex gap-3 items-start text-stone-700 leading-relaxed">
                      <span className="text-[#F2CC8F] mt-2 text-[8px]">●</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recomendaciones */}
      <section id="recomendaciones" className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-16 max-w-3xl">
          <p className="text-eyebrow text-stone-500 mb-4">{RECOMENDACIONES.eyebrow}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900">
            {RECOMENDACIONES.title}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {RECOMENDACIONES.categories.map((cat) => (
            <div
              key={cat.label}
              className="flex gap-6 items-start p-6 border border-stone-200 bg-white"
              data-testid={`experiencia-rec-${cat.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <i className={`fa-solid fa-${cat.icon} text-2xl text-[#E07A5F] mt-1`} />
              <div>
                <h3 className="font-serif-display text-xl text-stone-900 mb-1">{cat.label}</h3>
                <p className="text-stone-600 leading-relaxed text-[15px]">{cat.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-24 md:py-32 border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <p className="text-eyebrow text-stone-500 mb-4">Preguntas frecuentes</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-stone-900">
              Antes de reservar,
              <br />
              <span className="italic text-stone-500">te contamos todo con transparencia.</span>
            </h2>
          </div>
          <div className="space-y-8">
            {FAQ_PUBLIC.map((item) => (
              <div key={item.q} className="border-t border-stone-300 pt-6">
                <h3 className="font-serif-display italic text-2xl text-stone-900 mb-3">{item.q}</h3>
                <p className="text-stone-600 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-stone-900 text-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-eyebrow text-[#F2CC8F] mb-6">Reservar</p>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight mb-10 max-w-3xl mx-auto">
            Aquí quiero pasar un fin de semana
            <br />
            <span className="italic text-[#F2CC8F]">con mi familia.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#villas"
              data-testid="experiencia-final-cta-reservar"
              className="inline-flex items-center justify-center gap-2 bg-[#E07A5F] hover:bg-[#D3674B] text-white px-10 py-4 text-sm uppercase tracking-[0.22em] font-semibold transition-all hover:-translate-y-0.5"
            >
              Ver disponibilidad
            </Link>
            <button
              type="button"
              onClick={() => openWA("¡Hola! Vengo del recorrido de la experiencia. Quiero saber más sobre Villas Pura Vida.")}
              data-testid="experiencia-final-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 border border-white/70 text-white hover:bg-white hover:text-stone-900 px-8 py-4 text-sm uppercase tracking-[0.22em] font-semibold transition-all"
            >
              <i className="fa-brands fa-whatsapp" />
              Hablar por WhatsApp
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
